import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { CreateIntegrationButton } from '../../../../components/molecules/IntegrationButton';
import {
  analyticsEventActions,
  analyticsEventCategory,
} from '../../../../utils/constants';
import { Analytics } from '../../../../utils';
import { PlatformScreen } from '../../../../../shared/interfaces';
import { Spinner } from './Spinner';

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 0.3rem 1rem;
  justify-content: center;
  flex-wrap: nowrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledActionButton = styled.button`
  position: relative;
  flex: 0 0 auto;
  border: 0.0625rem solid #d7d3d0;
  background: #ffffff;
  max-height: 2.3125rem;
  border-radius: 0.625rem;
  padding: 0.5rem;
  font-size: 0.875rem;
  color: #57534e;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #f7f7f7;
  }
`;

const StyledSpinnerOverlay = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

function ActionButton({
  children,
  isLoading,
  label,
  integration,
  eventAction,
  ...rest
}: React.ComponentPropsWithoutRef<'button'> & {
  isLoading?: boolean;
  label?: string;
  integration?: unknown;
  eventAction?: string;
}) {
  return (
    <StyledActionButton {...rest}>
      <span style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
        {children}
      </span>
      {isLoading && (
        <StyledSpinnerOverlay>
          <Spinner size="0.875rem" color="#57534e" />
        </StyledSpinnerOverlay>
      )}
    </StyledActionButton>
  );
}

type Props = {
  screen: PlatformScreen;
};

export function FooterActions({ screen }: Readonly<Props>) {
  const navigate = useNavigate();
  const platform = screen.isMobile ? 'mobile' : 'desktop';
  const IntegrationButton = useMemo(
    () => CreateIntegrationButton(ActionButton),
    []
  );

  const onClickMore = useCallback(() => {
    navigate('/more');

    Analytics.event({
      action: analyticsEventActions.opens_more_page_via_more_icon,
      category: analyticsEventCategory.footer,
    });
  }, [navigate]);

  return (
    <Actions>
      <IntegrationButton
        platform={platform}
        service="schedule"
        label="Schedule Tour"
        fallback={() => navigate('/schedule')}
      />
      <IntegrationButton
        platform={platform}
        service="contact"
        label="Contact Property"
        fallback={() => navigate('/contact')}
      />
      <IntegrationButton
        platform={platform}
        label="More"
        service="more"
        fallback={onClickMore}
      />
    </Actions>
  );
}
